import React, { useState } from 'react'
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Modal,
} from '@mui/material'
import { useAuthInfo } from '@propelauth/react'
import { inviteUserToOrganization } from '../../../../../utils/utils'

function InviteUserModal({ open, onClose, onInviteSuccess }) {
    const [email, setEmail] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState('')

    const authInfo = useAuthInfo()
    const orgs = authInfo.orgHelper.getOrgs()
    const orgId = orgs[0]?.orgId

    const handleInvite = async () => {
        if (!email || !email.trim()) {
            setError('Please enter an email address')
            return
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email)) {
            setError('Please enter a valid email address')
            return
        }

        setIsSubmitting(true)
        setError('')

        try {
            const orgAccessToken = await authInfo.tokens.getAccessTokenForOrg(orgId)
            const jsonPayload = {
                email: email.trim()
            }

            await inviteUserToOrganization(jsonPayload, orgAccessToken)

            // Call success callback if provided
            if (onInviteSuccess) {
                await onInviteSuccess()
            }

            // Reset form and close modal
            setEmail('')
            onClose()
        } catch (error) {
            console.error('Error inviting user:', error)
            setError('Failed to send invitation. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleClose = () => {
        setEmail('')
        setError('')
        onClose()
    }

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !isSubmitting) {
            handleInvite()
        }
    }

    return (
        <Modal
            open={open}
            onClose={handleClose}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100vw',
                height: '100vh',
                zIndex: 1300
            }}
        >
            <Box
                sx={{
                    background: '#f9fafb',
                    borderRadius: 2,
                    boxShadow: '0 10px 40px rgba(0,0,0,0.16)',
                    width: '400px',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative'
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <Box sx={{
                    px: 3,
                    pt: 3,
                    pb: 2,
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    <IconButton
                        size="small"
                        onClick={handleClose}
                        sx={{
                            color: '#111827',
                            padding: 0,
                            width: 'fit-content',
                            mb: 2,
                            '&:hover': {
                                backgroundColor: 'transparent'
                            }
                        }}
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </IconButton>
                    <Typography sx={{
                        fontWeight: 700,
                        fontSize: 20,
                        color: '#1F2937',
                        lineHeight: 1,
                        mb: 1
                    }}>
                        Invite user
                    </Typography>
                    <Typography sx={{
                        fontSize: 14,
                        color: '#6B7280',
                        lineHeight: '20px',
                        mb: 3
                    }}>
                        Send an invitation to join your workspace
                    </Typography>

                    {/* Email input */}
                    <Box sx={{ mb: 3 }}>
                        <Typography sx={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: '#374151',
                            mb: 1
                        }}>
                            Email address
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="user@example.com"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value)
                                setError('')
                            }}
                            onKeyPress={handleKeyPress}
                            error={!!error}
                            helperText={error}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#fff',
                                    borderRadius: '4px',
                                    fontSize: 14,
                                    '& fieldset': {
                                        borderColor: '#E5E7EB',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#D1D5DB',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#1F477D',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    padding: '10px 14px',
                                },
                                '& .MuiFormHelperText-root': {
                                    fontSize: 12,
                                    marginLeft: 0,
                                    marginTop: '4px'
                                }
                            }}
                        />
                    </Box>

                    {/* Action buttons */}
                    <Box sx={{
                        display: 'flex',
                        gap: 1.5,
                        justifyContent: 'flex-end'
                    }}>
                        <Button
                            variant="outlined"
                            onClick={handleClose}
                            disabled={isSubmitting}
                            sx={{
                                borderRadius: '4px',
                                borderColor: '#E5E7EB',
                                color: '#374151',
                                fontWeight: 500,
                                fontSize: 14,
                                px: 2.5,
                                py: 1,
                                textTransform: 'none',
                                '&:hover': {
                                    borderColor: '#D1D5DB',
                                    backgroundColor: '#F9FAFB',
                                },
                                '&:disabled': {
                                    borderColor: '#E5E7EB',
                                    color: '#9CA3AF',
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleInvite}
                            disabled={isSubmitting || !email.trim()}
                            sx={{
                                borderRadius: '4px',
                                backgroundColor: '#1F477D',
                                color: '#FFFFFF',
                                fontWeight: 500,
                                fontSize: 14,
                                px: 2.5,
                                py: 1,
                                textTransform: 'none',
                                boxShadow: 'none',
                                '&:hover': {
                                    backgroundColor: '#183864',
                                    boxShadow: 'none'
                                },
                                '&:disabled': {
                                    backgroundColor: '#E5E7EB',
                                    color: '#9CA3AF',
                                }
                            }}
                        >
                            {isSubmitting ? 'Sending...' : 'Send invite'}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Modal>
    )
}

export default InviteUserModal
